import type { SessionCampaign } from "../campaign";
import type { AnswerOption, Question, Settings } from "../types";

// UC-1a/UC-5 pure session-state layer. Domain model Section 2: DiscoverySession is in-flight,
// KV-backed, TTL-bound state. This module is deliberately I/O-free — no KV, no D1, no HTTP — so
// the flow's mechanics can be built and tested independently of the presentation layer and ahead
// of the still-open Session API route contracts (checklist Section 6). The orchestration layer
// that reads/writes this to KV under a real API route is a separate, later task.

export type SessionState =
  | "started"
  | "answering"
  | "answers_complete"
  | "matching"
  | "matched"
  | "fallback"
  | "opted_out"
  | "abandoned"
  | "handed_off";

/** The visitor's contact info, for a `pii`-type question. Carried as its own field rather than
 * packed into selectedOptionIds (the single-string email hack this replaced) — structured data
 * deserves a structured shape. */
export interface PiiValue {
  firstName: string;
  lastName: string;
  email: string;
}

export interface AnswerRecord {
  questionId: string;
  selectedOptionIds: string[];
  isNoneOfThese: boolean;
  /** Set only for a `pii`-type question's answer. selectedOptionIds stays empty for these. */
  piiValue?: PiiValue;
  answeredAt: string;
  /** Set when a later answer to the same question supersedes this one. Superseded records are
   * retained, not overwritten or removed (domain model Section 8). */
  supersededAt?: string;
}

/** ADR-011 Section 5: "the selection trace ... which questions were asked, in what order, why,
 * which mode each was, and why the flow stopped." Appended by the orchestration layer
 * (src/session/adaptive-flow.ts) each time a question is chosen to be asked next — before it has
 * been answered, so the most recent entry with no corresponding live answer is the question
 * currently pending (see pendingQuestionId below). Under the deterministic fixed flow (mock-data
 * presentation, or any session state built directly via this module without going through the
 * adaptive planner) this stays empty, which is fine: every function below degrades to the old
 * order-based behavior when it is. */
export interface AskedQuestionRecord {
  questionId: string;
  /** "pinned" for a question inserted at its declared Question.pinnedPosition regardless of the
   * selector; "narrowing"/"discriminating" mirror the selected/fallback question's own
   * excludeFromFiltering flag, same convention as question-selector.ts's PoolQuestionForPrompt. */
  mode: "pinned" | "narrowing" | "discriminating";
  /** The selector's own one-sentence rationale, when this was the selector's genuine pick.
   * Absent for a pinned insertion or a deterministic-fallback pick — neither involved a model. */
  rationale?: string;
  /** True when the deterministic fallback path picked this question (selector error, timeout,
   * invalid output, or spend ceiling tripped — ADR-011 Section 3), not the selector genuinely
   * answering. The trace of when the ADR-008 guarantee actually had to fire. */
  usedFallback: boolean;
  /** Set when a deterministic guard chose this question (or kept the flow going to ask it) rather
   * than the selector's own vote deciding it: `"attribute_split"` is ADR-011 Section 3's
   * attribute-split guard (2026-09-24). Absent for a genuine selector pick, a pinned insertion and
   * a deterministic-fallback pick. Lands in D1's `selection_trace_json` unchanged, so eval and
   * telemetry can tell a guard pick from a model pick; it is also what the guard counts against
   * its per-session cap, which is why it survives on the record rather than only in a log line. */
  forcedBy?: ForcedBy;
  /** Set only for an optionsFromAttribute question (docs/plans/2026-10-08-options-from-attribute.md):
   * the options built from the candidate set at the moment it was asked, i.e. exactly what the
   * visitor saw. Stored rather than rebuilt, because the candidate set can change afterwards and
   * the answer must keep resolving to the label the visitor actually chose. Read through
   * optionsForQuestion below, never directly. */
  offeredOptions?: AnswerOption[];
  askedAt: string;
}

/**
 * The options a question actually had in this session: the stored offeredOptions for an
 * optionsFromAttribute question, otherwise the authored ones. Every place that turns an answer's
 * option ids into labels, or checks a submitted id, goes through this; reading `question.options`
 * directly would silently drop an optionsFromAttribute answer, since its authored options are
 * always empty. Takes only the trace so callers that hold a session and callers that hold just
 * the trace (telemetry) can share it.
 */
export function optionsForQuestion(askedQuestions: AskedQuestionRecord[], question: Question): AnswerOption[] {
  if (!question.optionsFromAttribute) return question.options;
  return askedQuestions.find((aq) => aq.questionId === question.id)?.offeredOptions ?? [];
}

/** Labels for an answer's selected option ids, via optionsForQuestion. Unknown ids are dropped. */
export function answerLabels(askedQuestions: AskedQuestionRecord[], question: Question, selectedOptionIds: string[]): string[] {
  const byId = new Map(optionsForQuestion(askedQuestions, question).map((o) => [o.id, o.label]));
  return selectedOptionIds.map((id) => byId.get(id)).filter((l): l is string => !!l);
}

/** Mirrors question-selector.ts's SelectorForcedBy, kept local for the same reason StopReason
 * below is. */
export type ForcedBy = "attribute_split";

/** Mirrors question-selector.ts's SelectorStopReason plus nothing extra — kept as its own local
 * literal type rather than importing across the session/matching boundary, same "session stays
 * decoupled from matching" convention src/matching/types.ts's own doc comments describe in the
 * other direction. The two are structurally identical by construction; adaptive-flow.ts is the
 * only place that has to know that. */
export type StopReason = "max_questions_reached" | "pool_exhausted" | "goal_met";

/** 2026-09-24 plan, Task 4: mirrors adaptive-flow.ts's SelectorProposal, same "session stays
 * decoupled from matching" convention as StopReason/ForcedBy above — what the selector proposed
 * (its next question, that question's mode and rationale, and its raw goalMet vote) on the turn
 * the session actually stopped. Only ever set alongside stopReason "goal_met" (the only stop that
 * happens after a genuine vote exists); absent for pool_exhausted/max_questions_reached and for any
 * session that stopped without reaching the selector at all. */
export interface StopProposal {
  questionId: string;
  questionMode: "narrowing" | "discriminating";
  rationale: string;
  goalMet: boolean;
}

export interface DiscoverySessionState {
  sessionId: string;
  state: SessionState;
  startedAt: string;
  lastActivityAt: string;
  configVersionId: string;
  answers: AnswerRecord[];
  abandonedAtQuestionId?: string;
  /** ADR-011 Section 5's selection trace. Always present (never undefined) so every consumer can
   * treat it as a plain array; empty for a session that predates this field or never went through
   * the adaptive planner. */
  askedQuestions: AskedQuestionRecord[];
  /** Set once the flow has decided to stop asking questions (markAnswersComplete below). Absent
   * for a session that reached answers_complete via the old "every active question answered"
   * path, or hasn't stopped yet. */
  stopReason?: StopReason;
  /** 2026-09-24 plan, Task 4: the selector's own proposal on the turn the session stopped, when one
   * existed (markAnswersComplete below). Cleared on the same re-plan path as stopReason — a changed
   * answer means the recorded proposal no longer describes anything real. */
  stopProposal?: StopProposal;
  /** True when the visitor started this session from the results screen's "Got another problem?"
   * button rather than from the CTA. A new session in every other respect; carried only so
   * telemetry (session_records.is_restart) can tell repeat sessions from first-time ones. Absent
   * on sessions that predate it, which read as false. */
  isRestart?: boolean;
  /** docs/plans/2026-10-13-campaigns.md: the campaign live on the page the session started from,
   * resolved once at start. Absent when there wasn't one. */
  campaign?: SessionCampaign;
}

export function startSession(
  sessionId: string,
  configVersionId: string,
  now: Date,
  options: { isRestart?: boolean; campaign?: SessionCampaign } = {}
): DiscoverySessionState {
  const nowIso = now.toISOString();
  return {
    sessionId,
    state: "started",
    startedAt: nowIso,
    lastActivityAt: nowIso,
    configVersionId,
    answers: [],
    askedQuestions: [],
    ...(options.isRestart ? { isRestart: true } : {}),
    ...(options.campaign ? { campaign: options.campaign } : {}),
  };
}

/**
 * ADR-011 Section 3/5: appends a question to the selection trace at the moment it is chosen to be
 * asked next (before it has an answer) — the orchestration layer's job, not recordAnswer's, since
 * planning which question comes next is a separate concern from recording the answer to the one
 * that just did. Pure, like every other function here; the caller persists the result.
 */
export function appendAskedQuestion(session: DiscoverySessionState, record: Omit<AskedQuestionRecord, "askedAt">, now: Date): DiscoverySessionState {
  const nowIso = now.toISOString();
  return {
    ...session,
    askedQuestions: [...session.askedQuestions, { ...record, askedAt: nowIso }],
    lastActivityAt: nowIso,
  };
}

/** ADR-011 Section 3: the orchestration layer calls this once the planner decides to stop asking
 * questions, moving the session to answers_complete with the reason recorded for telemetry.
 * `stopProposal` (2026-09-24 plan, Task 4) carries the selector's own proposal for that turn, when
 * planNextQuestion obtained one (adaptive-flow.ts's PlanOutcome.proposal). */
export function markAnswersComplete(session: DiscoverySessionState, stopReason: StopReason, now: Date, stopProposal?: StopProposal): DiscoverySessionState {
  return { ...session, state: "answers_complete", stopReason, stopProposal, lastActivityAt: now.toISOString() };
}

/**
 * The question currently awaiting an answer, derived from the selection trace rather than stored
 * separately: the most recent askedQuestions entry with no live (non-superseded) answer yet. At
 * most one entry can ever be in that state, since the orchestration layer appends exactly one new
 * entry per turn, only after the previous one has been answered. Undefined once the flow has
 * stopped (every asked question has an answer and nothing new has been appended) or before
 * anything has been asked. Stateless with respect to its own history by construction (ADR-011
 * Section 5's resume consequence) — a resumed session just reads this back out of the same trace.
 */
export function pendingQuestionId(session: DiscoverySessionState): string | undefined {
  const live = liveAnswersByQuestionId(session);
  const last = session.askedQuestions[session.askedQuestions.length - 1];
  return last && !live.has(last.questionId) ? last.questionId : undefined;
}

/** Currently-live (non-superseded) answers, keyed by questionId, for the questions/completion
 * logic below. Answer history stays in session.answers; this is a read-time projection. */
function liveAnswersByQuestionId(session: DiscoverySessionState): Map<string, AnswerRecord> {
  const live = new Map<string, AnswerRecord>();
  for (const answer of session.answers) {
    if (!answer.supersededAt) live.set(answer.questionId, answer);
  }
  return live;
}

/** UC-1a: next active question, in `order`, not yet answered. Null once the active set is fully
 * answered (i.e. answers_complete, per isComplete below). */
export function currentQuestion(session: DiscoverySessionState, questions: Question[]): Question | null {
  const live = liveAnswersByQuestionId(session);
  const next = questions
    .filter((q) => q.active && !live.has(q.id))
    .sort((a, b) => a.order - b.order)[0];
  return next ?? null;
}

/**
 * UC-1a: record an answer, superseding any prior live answer to the same question rather than
 * overwriting it (domain model Section 8 — "a visitor can go back and change a prior answer").
 *
 * `isNoneOfThese` is taken as an explicit argument rather than inferred from the config. The
 * config has no structural per-option marker for "this is the none option" — a zero-tag-weight
 * option is valid for other reasons too (confirmed against config-json-schema.md), so it's not a
 * safe signal. The presentation layer renders the distinct "none of these" affordance and knows
 * unambiguously when it was chosen; this function trusts that signal rather than guessing at it.
 *
 * ADR-011 Section 5, "re-planning on a changed answer": when the answered question already has a
 * later entry in the selection trace — i.e. this is a visitor going back and changing an earlier
 * answer, not the normal forward case of answering whatever's currently pending — every
 * askedQuestions entry after it, and the (still-live) answers those questions collected, are
 * discarded. Discarded means superseded, exactly like any other changed answer: retained in the
 * record, just no longer live (domain model Section 8, "superseded answers are retained"). The
 * caller (src/session/adaptive-flow.ts) picks up from the truncated trace and plans a fresh next
 * question, which may differ from what would have come next before.
 */
export function recordAnswer(
  session: DiscoverySessionState,
  question: Question,
  input: { selectedOptionIds: string[]; isNoneOfThese: boolean; piiValue?: PiiValue },
  now: Date
): DiscoverySessionState {
  const nowIso = now.toISOString();

  const askedIndex = session.askedQuestions.findIndex((aq) => aq.questionId === question.id);
  // A re-plan is either the usual "there are later asked questions to discard" case, or a change
  // to any already-asked question once the flow has already stopped (answers_complete): nothing
  // is pending in that state by definition, so there's no later trace entry to truncate away, but
  // the stop decision itself may have depended on the answer being changed and needs revisiting.
  const isReplan = askedIndex !== -1 && (askedIndex < session.askedQuestions.length - 1 || session.state === "answers_complete");

  let askedQuestions = session.askedQuestions;
  let answers = session.answers;

  if (isReplan) {
    const discardedIds = new Set(askedQuestions.slice(askedIndex + 1).map((aq) => aq.questionId));
    askedQuestions = askedQuestions.slice(0, askedIndex + 1);
    answers = answers.map((a) => (discardedIds.has(a.questionId) && !a.supersededAt ? { ...a, supersededAt: nowIso } : a));
  }

  answers = answers.map((a) => (a.questionId === question.id && !a.supersededAt ? { ...a, supersededAt: nowIso } : a));
  answers = [
    ...answers,
    {
      questionId: question.id,
      selectedOptionIds: input.isNoneOfThese ? [] : input.selectedOptionIds,
      isNoneOfThese: input.isNoneOfThese,
      piiValue: input.piiValue,
      answeredAt: nowIso,
    },
  ];

  return {
    ...session,
    answers,
    askedQuestions,
    // A re-plan reopens the flow even from answers_complete (the visitor went back after reaching
    // the end but before completing), since the truncated trace means the planner may no longer
    // stop where it previously did; stopReason goes with it, since it no longer describes anything
    // real until the planner runs again.
    state: isReplan || session.state === "started" ? "answering" : session.state,
    stopReason: isReplan ? undefined : session.stopReason,
    stopProposal: isReplan ? undefined : session.stopProposal,
    lastActivityAt: nowIso,
  };
}

/**
 * UC-1a: answers_complete is reached only once every active question is answered, including any
 * active pii-type question when Settings.requireEmailToReveal is on (domain model Section 2 —
 * the gate is enforced by the flow reaching its end, not a separate checkpoint before the reveal).
 */
export function isComplete(session: DiscoverySessionState, questions: Question[], settings: Pick<Settings, "requireEmailToReveal">): boolean {
  const live = liveAnswersByQuestionId(session);
  const active = questions.filter((q) => q.active);
  return active.every((q) => {
    if (q.answerType === "pii" && !settings.requireEmailToReveal) return true;
    return live.has(q.id);
  });
}

/** UC-5 extension 2a: an explicit opt-out is a distinct terminal state from abandoned. The
 * visitor made a decision and continued into the site; someone who closes the tab did not. */
export function optOut(session: DiscoverySessionState, now: Date): DiscoverySessionState {
  return { ...session, state: "opted_out", lastActivityAt: now.toISOString() };
}

/**
 * UC-1a: a session with no activity for sessionTtlHours transitions to abandoned.
 * `abandonedAtQuestionId` records where, which is the number the email gate's fate depends on
 * (domain model Section 2). No-ops (returns the session unchanged) if not yet expired, already
 * terminal, or mid-generation (matching is a short-lived server-side step, not something a
 * visitor can "abandon" by going idle).
 */
export function checkAbandonment(session: DiscoverySessionState, now: Date, questions: Question[], sessionTtlHours: number): DiscoverySessionState {
  const terminal: SessionState[] = ["opted_out", "abandoned", "handed_off", "matching"];
  if (terminal.includes(session.state)) return session;

  const lastActivity = new Date(session.lastActivityAt).getTime();
  const ttlMs = sessionTtlHours * 60 * 60 * 1000;
  if (now.getTime() - lastActivity < ttlMs) return session;

  // ADR-011: under the adaptive flow the visitor's actual pending question can differ from the
  // plain order-based guess (the selector does not ask questions in `order`), so the selection
  // trace is the correct source when one exists. Fall back to the order-based guess for a session
  // built without going through the adaptive planner at all (pre-ADR-011 sessions, and any session
  // state assembled directly via this module in tests or the mock-data presentation path).
  const pending = pendingQuestionId(session) ?? currentQuestion(session, questions)?.id;
  return {
    ...session,
    state: "abandoned",
    lastActivityAt: now.toISOString(),
    abandonedAtQuestionId: pending,
  };
}

export function restartSession(session: DiscoverySessionState, now: Date): DiscoverySessionState {
  const nowIso = now.toISOString();
  return {
    ...session,
    state: "started",
    answers: [],
    askedQuestions: [],
    stopReason: undefined,
    stopProposal: undefined,
    startedAt: nowIso,
    lastActivityAt: nowIso,
    abandonedAtQuestionId: undefined,
  };
}

/**
 * The telemetry-safe view of a session's answers (domain model Section 4). Filters out any
 * pii-type question's answer entirely — first name, last name, and email route to ContactCapture
 * only and must never reach SessionRecord.answersJson. This is the single most likely route for
 * PII to reach durable storage on this project (per the domain model), which is why it is a
 * function with a regression test rather than a convention callers are trusted to follow.
 */
export function toAnswersJson(session: DiscoverySessionState, questions: Question[]): Record<string, { selectedOptionIds: string[]; isNoneOfThese: boolean }> {
  const piiQuestionIds = new Set(questions.filter((q) => q.answerType === "pii").map((q) => q.id));
  const live = liveAnswersByQuestionId(session);
  const result: Record<string, { selectedOptionIds: string[]; isNoneOfThese: boolean }> = {};
  for (const [questionId, answer] of live) {
    if (piiQuestionIds.has(questionId)) continue;
    result[questionId] = { selectedOptionIds: answer.selectedOptionIds, isNoneOfThese: answer.isNoneOfThese };
  }
  return result;
}
